import { ASSET_TYPE, FWKIcons } from '@desp-aas/desp-ui-fwk';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { BiNetworkChart } from 'react-icons/bi';
import { BsBraces, BsDatabase, BsDisplay, BsFileText, BsMortarboard, BsSearch, BsUpload } from 'react-icons/bs';
import { FaBriefcase, FaCode, FaFlask, FaLandmark } from 'react-icons/fa6';
import { NavLink } from 'react-router-dom';
import AssetCardNewLoading from '../../../components/AssetCardNew/AssetCardNewLoading';
import AssetTypeModal, { ModalMode } from '../AssetTypeModal';
import HomeAssetCard from './HomeAssetCard';
import './Sections.css';
import { usePopularAssets } from './usePopularAssets';

type FeatureCard = { icon: React.ReactNode; title: string; text: string; color: string; cta: string; to?: string; mode?: ModalMode };

const cards: FeatureCard[] = [
    { icon: <BsSearch />, title: 'Find and use assets', text: 'Discover datasets, applications, models and code. Reuse what you need — accelerate your work.', color: 'rgb(239,43,137)', mode: 'explore', cta: 'Explore catalog' },
    { icon: <BsUpload />, title: 'Publish your asset', text: 'Share your data, code, models and applications with clear descriptions and metadata. Make your work discoverable and citable.', color: 'rgb(123,52,219)', mode: 'publish', cta: 'Start publishing' },
    { icon: <BiNetworkChart />, title: 'Collaborate and improve', text: 'Contribute to existing assets, provide feedback and suggest improvements. Build better solutions together.', color: 'rgb(20,143,150)', to: '/documentation', cta: 'Learn more' },
];

const audiences = [
    { icon: <FaFlask />, role: 'Scientists & Researchers', value: 'Find reliable datasets, test models, reuse code, gain visibility.' },
    { icon: <FaCode />, role: 'Data Scientists & EO Engineers', value: 'Access ready-to-use code, notebooks, pipelines and prepared data.' },
    { icon: <FaBriefcase />, role: 'Industry & Service Providers', value: 'Showcase expertise, publish demonstrators, collect feedback and contributions.' },
    { icon: <FaLandmark />, role: 'Institutions & Decision-Makers', value: 'Discover concrete use cases, visualise results and identify reusable solutions.' },
];

const assetTypeCards = [
    { icon: <BsDatabase />, color: 'rgb(239,43,137)', role: 'Datasets', value: 'Geospatial, climate, environmental and EO data ready to explore, download and integrate into your workflows.', to: '/catalog/dataset' },
    { icon: <BsDisplay />, color: 'rgb(20,143,150)', role: 'Applications', value: 'Ready-to-run tools, dashboards and services built on top of scientific data and models.', to: '/catalog/application' },
    { icon: <BsBraces />, color: 'rgb(123,52,219)', role: 'Models', value: 'Trained ML models, simulation frameworks and algorithms for Earth system and climate analysis.', to: '/catalog/model' },
    { icon: <BsFileText />, color: 'rgb(247,164,56)', role: 'Documents', value: 'Scientific papers, technical reports, methodologies and documentation linked to assets.', to: '/catalog/paper' },
    { icon: <BsMortarboard />, color: 'rgb(99,193,224)', role: 'Courses', value: 'Tutorials, guided notebooks and learning material to help you get started with DestinE tools and data.', to: '/catalog/course' },
];

const assetTypes = [
    { type: ASSET_TYPE.dataset, label: 'Datasets', color: 'rgb(239,43,137)' },
    { type: ASSET_TYPE.model, label: 'Models', color: 'rgb(123,52,219)' },
    { type: ASSET_TYPE.application, label: 'Applications', color: 'rgb(20,143,150)' },
    { type: ASSET_TYPE.paper, label: 'Documents', color: 'rgb(247,164,56)' },
];

function AudienceGrid() {
    return (
        <div className="home-audience-grid">
            {audiences.map((a, i) => (
                <div className="home-audience-card" key={i}>
                    <div className="home-audience-icon">{a.icon}</div>
                    <div className="home-audience-role">{a.role}</div>
                    <div className="home-audience-value">{a.value}</div>
                </div>
            ))}
        </div>
    );
}

export default function SectionsMain() {
    const rootRef = useRef<HTMLDivElement>(null);
    const [modal, setModal] = useState<ModalMode | null>(null);

    useEffect(() => {
        const root = rootRef.current;
        const container = document.getElementById('app');
        if (!root || !container) return;

        const items = root.querySelectorAll<HTMLElement>(
            '.home-feature-card, .home-audience-card, .home-asset-type-card, ' +
            '[class*="home-audience-title"], .home-variant-label, ' +
            '.home-popular-header, .home-popular-track-wrap'
        );

        items.forEach(el => {
            el.classList.add('sm-reveal');
            const siblings = Array.from(
                el.parentElement?.querySelectorAll<HTMLElement>(':scope > .sm-reveal') ?? []
            );
            const sibIdx = siblings.indexOf(el);
            if (sibIdx > 0) el.style.transitionDelay = `${sibIdx * 0.09}s`;
        });

        let scrollingDown = true;
        let lastScrollTop = container.scrollTop;
        const onScroll = () => {
            const st = container.scrollTop;
            if (st !== lastScrollTop) scrollingDown = st > lastScrollTop;
            lastScrollTop = st;
        };
        container.addEventListener('scroll', onScroll, { passive: true });

        const showInstant = (el: HTMLElement) => {
            el.style.transition = 'none';
            el.classList.add('sm-revealed');
            requestAnimationFrame(() => { el.style.transition = ''; });
        };

        const observer = new IntersectionObserver(
            entries => entries.forEach(e => {
                if (!e.isIntersecting) return;
                const el = e.target as HTMLElement;
                if (scrollingDown) {
                    el.style.setProperty('--sm-transform', 'translateY(28px)');
                    requestAnimationFrame(() => el.classList.add('sm-revealed'));
                } else {
                    showInstant(el);
                }
                observer.unobserve(el);
            }),
            { threshold: 0.12 }
        );
        items.forEach(el => observer.observe(el));
        return () => { observer.disconnect(); container.removeEventListener('scroll', onScroll); };
    }, []);

    return (
        <div ref={rootRef}>
            {/* Feature cards */}
            <section className="home-feature-section">
                <div className="section">
                    <div className="home-feature-grid">
                        {cards.map((c, i) => {
                            const style = { '--c': c.color } as React.CSSProperties;
                            const inner = (
                                <>
                                    <div className="home-feature-glow" />
                                    <div className="home-feature-icon-wrap">
                                        <div className="home-feature-icon">{c.icon}</div>
                                    </div>
                                    <div className="home-feature-title">{c.title}</div>
                                    <div className="home-feature-text">{c.text}</div>
                                    <div className="home-feature-cta">{c.cta} →</div>
                                </>
                            );
                            return c.mode
                                ? <button type="button" className="home-feature-card" key={i} style={style} onClick={() => setModal(c.mode!)}>{inner}</button>
                                : <NavLink to={c.to!} className="home-feature-card" key={i} style={style}>{inner}</NavLink>;
                        })}
                    </div>
                </div>
            </section>

            {/* Who is it for */}
            <section className="home-audience-section">
                <div className="section">
                    <div className="home-audience-header">
                        <h2 className="home-audience-title" style={{ textTransform: 'none' }}>
                            Who is it<br />
                            <span className="gradient-text" style={{ textTransform: 'uppercase' }}>for?</span>
                        </h2>
                        <AudienceGrid />
                    </div>
                </div>
            </section>

            {/* Asset types */}
            <section className="home-audience-section">
                <div className="section">
                    <div className="home-audience-header home-audience-header--reversed">
                        <h2 className="home-audience-title" style={{ textTransform: 'none' }}>
                            What to<br />
                            <span className="gradient-text" style={{ textTransform: 'uppercase' }}>find?</span>
                        </h2>
                        <div className="home-audience-grid home-asset-type-grid">
                            {assetTypeCards.map((a, i) => (
                                <NavLink to={a.to} className="home-audience-card" key={i}>
                                    <div className="home-audience-icon">{a.icon}</div>
                                    <div className="home-audience-role">{a.role}</div>
                                    <div className="home-audience-value">{a.value}</div>
                                </NavLink>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Popular assets */}
            {assetTypes.map(({ type, label, color }) => (
                <section className="home-popular-section" key={type}>
                    <div className="section">
                        <PopularMain type={type} label={label} color={color} />
                    </div>
                </section>
            ))}

            <AssetTypeModal mode={modal} onClose={() => setModal(null)} />
        </div>
    );
}

function PopularMain({ type, label, color }: { type: ASSET_TYPE; label: string; color: string }) {
    const { assets, loading, offset, isMobile, prev, next, maxOffset } = usePopularAssets(type);
    return (
        <div className="home-popular" style={{ '--pc': color } as React.CSSProperties}>
            <div className="home-popular-bg-label">{label.toUpperCase()}</div>
            <div className="home-popular-header">
                <h2 className="home-popular-title">Popular <span style={{ color }}>{label}</span></h2>
                <div className="home-popular-nav">
                    <button className={classNames('home-popular-btn', { disabled: offset === 0 })} onClick={prev}>{FWKIcons.letterChevronLeft}</button>
                    <button className={classNames('home-popular-btn', { disabled: offset >= maxOffset })} onClick={next}>{FWKIcons.letterChevronRight}</button>
                </div>
            </div>
            <div className="home-popular-track-wrap">
                <div className="home-popular-track" style={{ transform: `translateX(calc(${offset} * ${isMobile ? '(-100% - 12px)' : '(-33.333% - 4px)'}))` }}>
                    {loading
                        ? Array.from({ length: 6 }).map((_, i) => <AssetCardNewLoading key={i} />)
                        : assets?.map((a) => <HomeAssetCard asset={a} key={a.id} type={type} />)}
                </div>
            </div>
        </div>
    );
}
